import RisingParagraphsContainer from '@/components/Giveback2019/RisingParagraphsContainer';
import { MOBILE_VIEW_BOX_HEIGHT, VIEW_BOX_HEIGHT } from '@/components/Giveback2019/SVGCompletePath';
import Layout from '@/components/shared/Layout';
import { SEO } from '@/components/shared/SEO';
import { sharedConfig } from '@/pageConfig/shared';
import { Box, Flex, font, usePageScroll } from '@lemonade-hq/cdk';
import { Button } from '@lemonade-hq/nuis';
import { useNavContext } from '@lemonade-hq/nuiza';
import { COLORS } from '@lemonade-hq/tokens';
import dynamic from 'next/dynamic';
import styled, { createGlobalStyle } from 'styled-components';
import useOptionalTween from '../hooks/useOptionalTween';
import useTrackPad from '../hooks/useTrackPad';
import {
  GIVEBACK2019_DESKTOP_BREAKPOINT as DESKTOP_BREAKPOINT,
  GIVEBACK2019_MOBILE_BREAKPOINT as MOBILE_BREAKPOINT,
} from '../utils/browser';
import { GIVEBACK_LANDING_PAGE } from '../utils/productAdaPages';
import { shareOnTwitter } from '../utils/sharing';

import { useShowHeaderCtaOnScroll } from '@/hooks/useShowHeaderCtaOnScroll';
import ogImage from '@/static/assets/giveback_2019/og.png';

const SVGCompletePath = dynamic(() => import('../components/Giveback2019/SVGCompletePath'), {
  ssr: false,
});

const BOTTOM_PADDING = 380;
const MOBILE_BOTTOM_PADDING = 200;

const GlobalStyle = createGlobalStyle`
    footer p {
        display: none;
    }
`;

const Container = styled(Flex)`
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    height: ${MOBILE_VIEW_BOX_HEIGHT + MOBILE_BOTTOM_PADDING}px;
    margin-top: 0;
  }

  margin-top: 50px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  height: ${VIEW_BOX_HEIGHT + BOTTOM_PADDING}px;
  position: relative;
  overflow: hidden;
`;

const TextsContainer = styled(Box)`
  @media (min-width: ${DESKTOP_BREAKPOINT}px) {
    width: 1060px;
  }

  @media (max-width: 340px) {
    width: 320px;
  }

  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  margin-left: auto;
  margin-right: auto;
  text-align: center;
  color: ${COLORS.night};
  width: 340px;
`;

const PathContainer = styled(Box)`
  @media (min-width: ${DESKTOP_BREAKPOINT}px) {
    top: 60px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    top: 150px;
  }

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    margin-left: auto;
    margin-right: auto;
    left: 0;
    right: 0;
    width: 1440px;
  }

  position: absolute;
`;

const BottomTextsContainer = styled(Box)`
  @media (min-width: ${DESKTOP_BREAKPOINT}px) {
    width: 650px;
  }

  position: absolute;
  bottom: 110px;
  left: 0;
  right: 0;
  margin-left: auto;
  margin-right: auto;
  text-align: center;
  color: ${COLORS.night};
  width: 340px;

  @media (max-width: 340px) {
    width: 320px;
  }
`;

const Title = styled.h1`
  @media (min-width: ${DESKTOP_BREAKPOINT}px) {
    ${font('alt', { fontSize: '7.6rem', lineHeight: '1.22' })};
  }

  @media (max-width: 340px) {
    ${font('alt', { fontSize: '3.6rem', lineHeight: '1.27' })};
  }

  ${font('alt', { fontSize: '4.4rem', lineHeight: '1.27' })};
  padding: 0 10px;
`;

const Subtitle = styled.h2`
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 0 20px;
    margin-top: 30px;
    ${font('alt', { fontSize: '1.4rem', lineHeight: '1.42' })};
  }

  margin-top: 50px;
  ${font('alt', { fontSize: '2.4rem' })};
`;

const BottomTitle = styled.h3`
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 0 20px;
    margin-top: 30px;
    ${font('main', {
      fontSize: '2.8rem',
      lineHeight: '1.28',
      fontWeight: 'bold',
    })};
  }

  ${font('main', { fontSize: '4.6rem', lineHeight: 1.34, fontWeight: 'bold' })};
`;

const BottomSubtitle = styled.h4`
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 0 20px;
    margin-top: 20px;
    ${font('main', { fontSize: '2rem', lineHeight: '1.4' })};
  }

  margin-top: 30px;
  margin-bottom: 60px;
  ${font('main', { fontSize: '2rem', lineHeight: 1.4 })};
`;

const ShareButton = styled(Button)`
  padding: 0;
  height: 40px;

  &:hover {
    color: ${COLORS.white};
    background-color: ${COLORS.pink};
    transition: all 0.3s ease-out;
  }
`;

const CTA = styled(Button)`
  @media (min-width: ${DESKTOP_BREAKPOINT}px) {
    padding-left: 35px;
    padding-right: 35px;
  }

  text-decoration: none;
`;

export default function Giveback2019Page() {
  const { nav } = useNavContext();
  useShowHeaderCtaOnScroll(true);

  const { isTrackPad } = useTrackPad();
  const { relativeScroll: t } = usePageScroll();

  const actualT = useOptionalTween(t, !isTrackPad, 10000);

  const CustomHeaderCTA = (
    <ShareButton
      minWidth={90}
      hugging={false}
      flavor="ghost"
      onClick={() =>
        shareOnTwitter(
          'I just helped donate $631,540.07 to charities with my Lemonade policy. Insurance will never be the same! #GoLemonade #Giveback2019 via @lemonade_inc',
          'https://bit.ly/2LIRCo8',
        )
      }
    >
      SHARE
    </ShareButton>
  );

  return (
    <Layout
      {...{ CustomHeaderCTA, hideAskUsAnything: true, pt: [90, 120, 160] }}
      page={GIVEBACK_LANDING_PAGE}
      {...sharedConfig['en-US'].layout}
    >
      <SEO
        title="The 2019 Lemonade Giveback"
        metaTitle="The 2019 Lemonade Giveback"
        description="$631,540.07 donated to 26 charities in the 2019 Lemonade Giveback. Here's how it works: When you sign up for an insurance policy, Lemonade takes a flat fee and treats the rest of that money as yours, not ours. We use it to pay claims, and give back what's left over to charities you choose, so we never fight over the same coin."
        ogImage={ogImage.src}
      />
      <GlobalStyle />
      <Container>
        <PathContainer>
          <SVGCompletePath relativeScroll={actualT} />
        </PathContainer>
        <TextsContainer>
          <Title>Six hundred thirty one thousand five hundred forty dollars and seven cents donated</Title>
          <Subtitle>Giveback 2019 impacts 26 charities chosen by the Lemonade community</Subtitle>
          <RisingParagraphsContainer relativeScroll={actualT} />
        </TextsContainer>
        <BottomTextsContainer>
          <BottomTitle>Join the Giveback Movement</BottomTitle>
          <BottomSubtitle>
            Hundreds of thousands of members already support causes they care about, simply by getting a Lemonade
            policy. As our Lemonade community grows, your social impact can become even stronger.
          </BottomSubtitle>
          {/* @ts-expect-error - styled components type issue */}
          <CTA as="a" flavor="primary" href={nav.giveback} glowing>
            Learn more about giveback
          </CTA>
        </BottomTextsContainer>
      </Container>
    </Layout>
  );
}
